import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type { FormColumn } from "../schemas/column-schema"

import { api } from "@/lib/api-client"
import { queryKeys } from "@/lib/query-keys"

import { useAIParserSession } from "./use-ai-parser-session"

type Data = {
  sessionId: string | null
  columns: FormColumn[]
}

function addColumns(data: Data): Promise<{ session_id: string }> {
  const { sessionId, columns } = data

  return api.post("/parser/add-columns", {
    session_id: sessionId,
    columns: columns.map((column) => ({
      name: column.name,
      data_type: column.dataType,
      description: column.description,
      option_type_id: column.optionTypeId,
      options: column.options,
    })),
  })
}

export function useAddColumnsMutation() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentSessionId, setCurrentSessionId, sessionType } = useAIParserSession()

  return useMutation({
    mutationFn: ({ columns }: { columns: FormColumn[] }) =>
      addColumns({ sessionId: currentSessionId, columns }),
    onSuccess: (data) => {
      setCurrentSessionId(data.session_id)
      void queryClient.invalidateQueries({
        queryKey: queryKeys.fullSession(currentSessionId ?? ""),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.sessionHistory(sessionType),
      })
      toast.success(t("pages.ai_parser.toasts.columns_added"))

      // Navigate to the correct session URL
      void navigate(`/ai-parser/chat/${data.session_id}`)
    },
  })
}
